import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/system";
import { DeleteBin, MailEmailLetter } from "@pmag/icons";
import { Button, IconButton, Typography } from "@pmag/ui-components";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useWishlist } from "@src/hooks/useWishlist";
import { AddToCartButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/addToCartButton/AddToCartButton";
import { AvailabilityStatus } from "@src/pages/subGroupDetail/subgroupDetailsListSection/availabilityStatus/AvailabilityStatus";
import { StyledTypography } from "@src/pages/subGroupDetail/subgroupDetailsListSection/sparePartsList/SparePartsList.styles";
import { getWishlistEntry, type WishlistEntryModel } from "@src/pages/wishlist/Wishlist.utils";
import { dispatchSendRequestEvent, SPFAvailabilityStatus } from "@src/utils/events";
import * as React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import {
    ArticleImage,
    ArticleInfoContainer,
    BottomContainer,
    RightContainer,
    RootContainer,
    StyledSelect,
    TopContainer,
} from "./SmallWishlistItem.styles";

interface SmallWishlistItemProps {
    showBorderBottom: boolean;
    wishlistEntry: WishlistEntryModel;
}

export const SmallWishlistItem: React.FunctionComponent<SmallWishlistItemProps> = ({ wishlistEntry, showBorderBottom }) => {
    const theme = useTheme();
    const { removeFromWishList } = useWishlist();
    const { hasArticleInfos, hasAddToCart, priceInfoDisclaimer, quantityInfoDisclaimer, sendRequestEnabled } = useAttributeConfig();

    const [amount, setAmount] = React.useState(wishlistEntry.quantity > 0 ? wishlistEntry.quantity : 1);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setTimeout(() => {
            setLoading(false);
        }, 5000);
    }, []);

    let modelDescription = `${wishlistEntry.model.brand?.name} ${wishlistEntry.model.description} / ${wishlistEntry.model.year}`;
    if (wishlistEntry.model.modelSpecifications) modelDescription += wishlistEntry.model.modelSpecifications.map((item) => ` / ${item.name}`);
    modelDescription += ` / ${wishlistEntry.model.articleNumber}`;

    const showAddToCartButton =
        hasArticleInfos &&
        hasAddToCart &&
        (wishlistEntry.clientArticleInfo?.availability === SPFAvailabilityStatus.Available ||
            wishlistEntry.clientArticleInfo?.availability === SPFAvailabilityStatus.ManufacturerAvailable);

    const showSendRequestButton =
        sendRequestEnabled &&
        (!hasArticleInfos ||
            wishlistEntry.clientArticleInfo?.availability === SPFAvailabilityStatus.NotAvailable ||
            wishlistEntry.clientArticleInfo?.availability === SPFAvailabilityStatus.Unknown);

    return (
        <RootContainer showBorderBottom={showBorderBottom}>
            <TopContainer>
                {wishlistEntry.article?.assetPath && <ArticleImage src={wishlistEntry.article.assetPath} />}

                <ArticleInfoContainer>
                    <Typography variant="heading-08">
                        {wishlistEntry.article?.name}

                        {wishlistEntry.quantity && (
                            <StyledTypography component="span" variant="body-short-01" color={theme.palette.core.grey._60}>
                                {`(${wishlistEntry.quantity})`}

                                {quantityInfoDisclaimer && <sup>**</sup>}
                            </StyledTypography>
                        )}
                    </Typography>

                    <Typography variant="helper-text-01">{wishlistEntry.article?.articleNumber}</Typography>

                    <Typography variant="helper-text-01" color={theme.palette.core.grey._80}>
                        {modelDescription}
                    </Typography>

                    {loading && !wishlistEntry.clientArticleInfo ? (
                        <Skeleton height={18} width={60} />
                    ) : (
                        <AvailabilityStatus availabilityStatus={wishlistEntry.clientArticleInfo?.availability ?? SPFAvailabilityStatus.Unknown} />
                    )}
                </ArticleInfoContainer>
            </TopContainer>

            <BottomContainer>
                <Typography variant="heading-08">
                    {wishlistEntry.clientArticleInfo && wishlistEntry.clientArticleInfo.price ? (
                        <>
                            <FormattedNumber
                                value={wishlistEntry.clientArticleInfo.price}
                                currency={wishlistEntry.clientArticleInfo.currency}
                                style="currency"
                            />

                            {priceInfoDisclaimer && <sup>*</sup>}
                        </>
                    ) : loading ? (
                        <Skeleton height={24} width={50} />
                    ) : (
                        <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.placeholder" defaultMessage="-" />
                    )}
                </Typography>

                <RightContainer>
                    {showAddToCartButton && wishlistEntry.article && wishlistEntry.model.brand && (
                        <>
                            <StyledSelect
                                select
                                value={amount}
                                onChange={(value) => setAmount(parseInt(value.target.value))}
                                variant="outlined"
                                SelectProps={{ native: true }}
                            >
                                {Array(20)
                                    .fill(null)
                                    .map((_, index) => (
                                        <option key={`amount-select-${index}`} value={index + 1}>{`${index + 1}`}</option>
                                    ))}
                            </StyledSelect>

                            <AddToCartButton
                                articleNumber={wishlistEntry.article.articleNumber}
                                amount={amount}
                                brand={wishlistEntry.model.brand.name}
                            />
                        </>
                    )}

                    {showSendRequestButton && wishlistEntry.article && wishlistEntry.model.brand && (
                        <Button
                            variant="secondary"
                            size="small"
                            icon={<MailEmailLetter size="small" />}
                            onClick={() =>
                                wishlistEntry.article &&
                                wishlistEntry.model.brand &&
                                dispatchSendRequestEvent(wishlistEntry.article.articleNumber, wishlistEntry.model.brand.shortName)
                            }
                        >
                            <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.sendRequest" defaultMessage="Send request" />
                        </Button>
                    )}

                    <IconButton variant="secondary" onClick={() => removeFromWishList(getWishlistEntry(wishlistEntry.id, wishlistEntry.model.id))}>
                        <DeleteBin size="small" />
                    </IconButton>
                </RightContainer>
            </BottomContainer>
        </RootContainer>
    );
};
